import { Injectable } from '@angular/core';
import { ProductService } from '../product.service';

export interface DiwaliCartLine {
  voucher_product_id: number;
  voucher_category_id: number;
  sku: string;
  name: string;
  denomination: number;
  qty: number;
  image: string;
}

@Injectable({
  providedIn: 'root'
})
export class DiwaliCartService {

  private _cart: DiwaliCartLine[] = [];
  private _userCode = '';
  private _eventCode = 'diwali';
  private _token = '';
  private syncTimer: any = null;

  constructor(private productService: ProductService) { }

  get cart(): DiwaliCartLine[] {
    return this._cart;
  }

  get cartTotal(): number {
    return this._cart.reduce((sum, line) => sum + line.denomination * line.qty, 0);
  }

  get cartQty(): number {
    return this._cart.reduce((sum, line) => sum + line.qty, 0);
  }

  get userCode(): string {
    return this._userCode;
  }

  init(userCode: string, eventCode: string, token: string): void {
    if (!userCode) {
      return;
    }
    this._userCode = userCode;
    this._eventCode = eventCode;
    this._token = token;
    this.loadFromLocal();
    if (this._cart.length === 0) {
      this.fetchFromDb();
    }
  }

  add(product: any, denomination: number, remainingAmount: number): boolean {
    if (this.cartTotal + denomination > remainingAmount) {
      return false;
    }

    const existing = this._cart.find(line =>
      line.voucher_product_id === product.voucher_product_id && line.denomination === denomination
    );

    if (existing) {
      existing.qty += 1;
    } else {
      this._cart.push({
        voucher_product_id: product.voucher_product_id,
        voucher_category_id: product.voucher_category_id,
        sku: product.sku || '',
        name: product.name || '',
        denomination: denomination,
        qty: 1,
        image: product.images || 'assets/images/default.jpg'
      });
    }

    this.persistLocal();
    this.syncToDb();
    return true;
  }

  changeQty(line: DiwaliCartLine, delta: number, remainingAmount: number): void {
    const next = line.qty + delta;

    if (next < 1) {
      this.remove(line);
      return;
    }

    if (delta > 0 && this.cartTotal + line.denomination > remainingAmount) {
      return;
    }

    line.qty = next;
    this.persistLocal();
    this.syncToDb();
  }

  remove(line: DiwaliCartLine): void {
    this._cart = this._cart.filter(item => item !== line);
    this.persistLocal();
    this.syncToDb();
  }

  removePlaced(placed: DiwaliCartLine[]): void {
    if (!placed.length) {
      return;
    }
    this._cart = this._cart.filter(line => !placed.includes(line));
    this.persistLocal();
    this.syncToDb();
  }

  clearLocal(): void {
    try {
      localStorage.removeItem(this.storageKey());
    } catch {
      // ignore storage errors
    }
  }

  private storageKey(): string {
    return this._userCode ? `diwaliCart:${this._userCode}` : 'diwaliCart';
  }

  private loadFromLocal(): void {
    try {
      const raw = localStorage.getItem(this.storageKey());
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          this._cart = parsed;
        }
      }
    } catch {
      this._cart = [];
    }
  }

  private persistLocal(): void {
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(this._cart));
    } catch {
      // ignore storage errors
    }
  }

  private fetchFromDb(): void {
    this.productService.getDiwaliCart(this._userCode, this._eventCode, this._token).subscribe({
      next: (res: any) => {
        const response = res?.response;
        if (response?.status === 1 && Array.isArray(response.data) && response.data.length) {
          this._cart = response.data.map((item: any) => ({
            voucher_product_id: Number(item.voucher_product_id),
            voucher_category_id: Number(item.voucher_category_id),
            sku: item.sku || '',
            name: item.name || '',
            denomination: Number(item.denomination),
            qty: Number(item.qty),
            image: item.image || 'assets/images/default.jpg'
          }));
          this.persistLocal();
        }
      }
    });
  }

  private syncToDb(): void {
    if (!this._userCode) {
      return;
    }
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
    }
    this.syncTimer = setTimeout(() => {
      this.productService.saveDiwaliCart(this._userCode, this._eventCode, this._cart, this._token).subscribe();
    }, 500);
  }
}